
import React, { useEffect, useState } from "react";
import {
    Modal,
    Button,
    Row,
    Col,
    Card,
    Badge,
    Spinner,
    Accordion,
    Table
} from "react-bootstrap";
import axios from "axios";
import { getLangName } from "../../helper";
import { PROOFING_URL } from "../../../redux/config";

const ComplianceReportModal = ({
    show,
    onClose,
    file_url,
    targetLangCode,
}) => {
    const [loading, setLoading] = useState(true);
    const [report, setReport] = useState(null);

    useEffect(() => {
        if (show) {
            fetchReport();
        }
    }, [show]);

    const convertSignedUrlToBase64 = async (signedUrl) => {
        const response = await fetch(signedUrl, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status}`);
        }

        const blob = await response.blob();

        if (blob.type !== 'application/pdf') {
            console.warn('Fetched file is not a PDF:', blob.type);
        }

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => {
                // Remove: data:application/pdf;base64,
                resolve(reader.result.split(',')[1]);
            };
            reader.onerror = reject;
        });
    };

    const [iframeUrl, setIframeUrl] = useState(null);

    useEffect(() => {
        if (!report) return;

        const blob = new Blob([report], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        setIframeUrl(url);
        setLoading(false);
        return () => URL.revokeObjectURL(url);
    }, [report]);



    // const fetchReport = async () => {
    //     setLoading(true);
    //     try {
    //         const base64PDF = await convertSignedUrlToBase64(file_url);

    //         const resData = await axios.post(
    //             `${PROOFING_URL}`,
    //             {
    //                 file: base64PDF,
    //                 language_code: targetLangCode
    //             }
    //         );
    //         setReport(resData?.data)


    //         // ✅ MOCK RESPONSE (your provided data)
    //         // setReport(MOCK_RESPONSE);
    //     } catch (err) {
    //         console.error(err);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // if (!report && loading) return null;

    // Group rules by category

    const fetchReport = async () => {
        setLoading(true);
        try {
            const base64PDF = await convertSignedUrlToBase64(file_url);

            const resData = await axios.post(
                PROOFING_URL,
                {
                    file: base64PDF,
                    language_code: targetLangCode
                },
                {
                    headers: {
                        Accept: "text/html"
                    },
                    responseType: "text"   // 👈 IMPORTANT
                }
            );

            setReport(resData.data); // HTML string
        } catch (err) {
            console.error(err);
        }
    };

    const groupedRules = report?.rule_checks?.reduce((acc, rule) => {
        acc[rule.rule_category] = acc[rule.rule_category] || [];
        acc[rule.rule_category].push(rule);
        return acc;
    }, {});

    return (
        <Modal show={show} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Compliance Analysis Report</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loading && (
                    <div className="text-center my-5">
                        <Spinner animation="border" role="status" />
                        <div className="mt-3 fw-semibold text-muted">
                            Analyzing…
                        </div>
                    </div>
                )}

                {iframeUrl && (
                    <iframe
                        src={iframeUrl}
                        title="Compliance Report"
                        style={{
                            width: "100%",
                            height: "100vh",
                            border: "none"
                        }}
                    />
                )}

                {!iframeUrl && !loading && report && (
                    <>
                        {/* HEADER */}
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <small className="text-muted">OVERALL SCORE</small>
                                        <h1 className="fw-bold">{report.overall_score}%</h1>
                                        <Badge bg="success">Language: {getLangName(report.language_code)}</Badge>
                                    </Col>

                                    <Col md={6} className="text-md-end mt-3 mt-md-0">
                                        <small className="text-muted">Analyzed At</small>
                                        <div className="fw-semibold">
                                            {new Date(report.analysis_timestamp).toLocaleString()}
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* CATEGORY SCORES */}
                        <Card className="mb-4">
                            <Card.Header className="fw-bold">
                                Category Scores
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {Object.entries(report.category_scores).map(
                                        ([key, value]) => (
                                            <Col md={3} sm={6} key={key} className="mb-3">
                                                <div className="border rounded p-3 text-center">
                                                    <div className="fw-semibold">{key}</div>
                                                    <h5 className="mb-0">{value}%</h5>
                                                </div>
                                            </Col>
                                        )
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* SUMMARY */}
                        <Card className="mb-4">
                            <Card.Header className="fw-bold">Summary</Card.Header>
                            <Card.Body>
                                <Row>
                                    {Object.entries(report.summary).map(([k, v]) => (
                                        <Col md={4} key={k} className="mb-2">
                                            <strong>{k.replaceAll("_", " ")}:</strong> {v}
                                        </Col>
                                    ))}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* RULE CHECKS */}
                        <Card className="mb-4">
                            <Card.Header className="fw-bold">
                                Rule Checks ({report.rule_checks.length})
                            </Card.Header>
                            <Card.Body>
                                <Accordion alwaysOpen>
                                    {Object.entries(groupedRules).map(
                                        ([category, rules], idx) => (
                                            <Accordion.Item eventKey={idx.toString()} key={category}>
                                                <Accordion.Header>
                                                    {category} ({rules.length})
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <Table bordered responsive size="sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Rule ID</th>
                                                                <th>Description</th>
                                                                <th>Status</th>
                                                                <th>Evidence</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rules.map(rule => (
                                                                <tr key={rule.rule_id}>
                                                                    <td>{rule.rule_id}</td>
                                                                    <td>{rule.rule_description}</td>
                                                                    <td>
                                                                        <Badge bg="success">
                                                                            {rule.compliance_status}
                                                                        </Badge>
                                                                    </td>
                                                                    <td>{rule.evidence}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        )
                                    )}
                                </Accordion>
                            </Card.Body>
                        </Card>

                        {report.issues && report.issues.length > 0 && (
                            <Card className="mb-4">
                                <Card.Header className="fw-bold text-danger">
                                    Issues
                                </Card.Header>
                                <Card.Body>
                                    {report.issues.map((issue, idx) => (
                                        <div key={idx} className="mb-3">
                                            <Badge bg="danger" className="me-2">
                                                {issue.severity}
                                            </Badge>
                                            <strong>{issue.category}</strong>
                                            <div className="mt-1">{issue.description}</div>
                                            <small className="text-muted">
                                                Location: {issue.location}
                                            </small>
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        )}

                        {report.recommendations && report.recommendations.length > 0 && (
                            <Card className="mb-4">
                                <Card.Header className="fw-bold">
                                    Recommendations
                                </Card.Header>
                                <Card.Body>
                                    <ul className="mb-0">
                                        {report.recommendations.map((rec, idx) => (
                                            <li key={idx}>{rec}</li>
                                        ))}
                                    </ul>
                                </Card.Body>
                            </Card>
                        )}

                        {report.strengths && report.strengths.length > 0 && (
                            <Card className="mb-4">
                                <Card.Header className="fw-bold text-success">
                                    Strengths
                                </Card.Header>
                                <Card.Body>
                                    <ul className="mb-0">
                                        {report.strengths.map((strength, idx) => (
                                            <li key={idx}>{strength}</li>
                                        ))}
                                    </ul>
                                </Card.Body>
                            </Card>
                        )}

                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ComplianceReportModal;
